import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, select, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { ObscuredData, ObscuredDataService } from '@se/components';
import { SeConstraintApplierService } from '@se/constraint';
import { HardcodedData, Json } from '@se/cp.common';
import {
    CancellationError,
    ClinicalRepositoryDataType,
    convertPropertiesToCamelCase,
    convertPropertiesToPascalCase,
    CpHttpService,
    EstensaBusWrapperService,
    EstensaWaitingService,
    isExtensaLockFault,
    RadioSettingsService,
    ReportingDataBuilder,
    seHeaderToolbarSetIsNavigateOnClickEnabled,
    seLogoClick,
    seReportApproveCompleted,
    seReportSave,
    seReportSaveCompleted,
    seReportSetCurrentExaminationIdsVisit,
    seReportSetImagesPerRowInPlaceholder,
    seReportSetIsPreview,
    seReportSetKeyImages,
    seReportSetPatientId,
    seSideNavComponentSetActiveComponents,
    seToolbarSetLeftSlider,
    seToolbarSetRightSlider,
    ThirdPartSupportService,
} from '@se/platform';
import {
    DataBinding,
    Logger,
    ModalDialogNotificationService,
    NotificationType,
    RouterStackService,
    tapAlways,
} from '@se/se.ui.html5';
import {
    estensaPostMessage,
    ObscuredTabType,
    THIRD_PART_POST_MESSAGES,
    THIRD_PART_STATUS_CODE,
    THIRD_PART_STATUS_DETAIL,
} from 'estensa-module';
import { isFunction } from 'lodash-es';
import {
    catchError,
    concat,
    defaultIfEmpty,
    EMPTY,
    exhaustMap,
    filter,
    finalize,
    firstValueFrom,
    forkJoin,
    from,
    lastValueFrom,
    map,
    Observable,
    of,
    switchMap,
    take,
    tap,
    withLatestFrom,
} from 'rxjs';

import {
    cpApplyNormalValues,
    cpApprove,
    cpApproveAddendum,
    cpApproveSaving,
    cpEmpty,
    cpInitialize,
    cpNavigationBack,
    cpNavSelectNode,
    cpObscurationEditRequest,
    cpPatchModel,
    cpSafeExit,
    cpSave,
    cpSaveCompleted,
    cpSelectNode,
    cpSetModel,
    cpShowErrorModal,
    cpSign,
    cpUndoValidation,
    cpUpdateAllNodes,
} from './cp.actions';
import { CpNodeService } from '../cp-node.service';
import { CpUiEngineService } from '../cp-ui-engine.service';
import { CpModelService } from '../cp-model.service';
import { CpDatasetService } from '../cp-dataset-service';
import { CpReportService, PartialError } from '../cp-report.service';
import { CpControlDataService } from '../cp-controls/control-data.service';
import { CpPageState } from './cp.state';
import { selectCpIsPageOpen, selectCpModules } from './cp.selectors';
import { CpEntityList } from '../cp-configuration/view-model';

export type ObservableOrAwaitable<T> = Observable<T> | Promise<T>;

export interface EffectResultImage {
    readonly type: 'image';
    id: string;
    data: string;
}

export interface EffectResultImageDelete {
    readonly type: 'image-delete';
    id: string;
}

export type EffectResult = EffectResultImage | EffectResultImageDelete;

function isEffectResultImage(res: EffectResult): res is EffectResultImage {
    return res.type === 'image';
}

function isEffectResultImageDelete(res: EffectResult): res is EffectResultImageDelete {
    return res.type === 'image-delete';
}

export type EventResult = EffectResult[] | void;

export type CpEffectType = 'calculations' | 'collectImages';

export type CpEffectHandler = () => ObservableOrAwaitable<EventResult>;

export type EffectThunk = () => Observable<unknown>;

const mkDefaultHandlers = () => ({ calculations: [], collectImages: [] });

/**
 * Linee guida per la scrittura di effetti:
 * - Gestire effetti solamente per le Custom Page, gli effetti globali vanno gestiti in se.platform.
 * - Catturare _SEMPRE_ gli errori, altrimenti la gestione dell'effetto si interrompe.
 * - Per effetti lanciati più volte contemporaneamente utilizzare exhaustMap, se si vuole che l'effetto venga eseguito una volta sola, altrimenti utilizzare switchMap.
 * - Risolvere o rigettare _SEMPRE_ le Promise, nel caso venga chiamato un servizio che non utilizzi Observables.
 * - Utilizzare { dispatch: false } solo ove necessario, in generale è preferibile ritornare un'azione.
 * - Non è possibile utilizare catchError al livello più alto, perché exhaustMap si interrompe in caso di errore.
 */
@Injectable()
export class CpEffects {
    private handlers: Record<CpEffectType, CpEffectHandler[]> = mkDefaultHandlers();

    constructor(
        private actions$: Actions,
        private constraintApplier: SeConstraintApplierService,
        private cpControlDataService: CpControlDataService,
        private cpDatasetService: CpDatasetService,
        private cpHttp: CpHttpService,
        private cpModelService: CpModelService,
        private cpNodeService: CpNodeService,
        private modalDialogNotificationService: ModalDialogNotificationService,
        private logger: Logger,
        private obscuredDataService: ObscuredDataService,
        private reportService: CpReportService,
        private reportingDataBuilder: ReportingDataBuilder,
        private routerStackService: RouterStackService,
        private settings: RadioSettingsService,
        private store: Store,
        private thirdPartSupportService: ThirdPartSupportService,
        private translate: TranslateService,
        private uiEngine: CpUiEngineService,
        private waitingService: EstensaWaitingService,
        estensaBus: EstensaBusWrapperService,
    ) {
        // Messaggi dalle terze parti
        estensaBus.busDataFired.subscribe((m) => this.onMessage(m));
    }

    // #region Effects

    initializePage$ = createEffect(() =>
        this.actions$.pipe(
            ofType(cpInitialize),
            switchMap((action) => this.onCpInitialize(action.page, action.isPreview ?? false)),
        ),
    );

    /**
     * Alla selezione di un nodo nel navigatore, scrolla il rollup fino al nodo.
     */
    navSelect$ = createEffect(() =>
        this.actions$.pipe(
            ofType(cpNavSelectNode),
            map((action) => {
                this.scrollPanelIntoView(action.id);

                return cpSelectNode({ id: action.id });
            }),
        ),
    );

    /**
     * Effeettua la pulizia dei vari servizi.
     */
    safeExit$ = createEffect(() =>
        this.actions$.pipe(
            ofType(cpSafeExit),
            tap(() => {
                try {
                    this.cpNodeService.safeExit();
                    this.cpModelService.safeExit();
                    this.cpDatasetService.safeExit();
                    this.cpControlDataService.safeExit();
                    this.handlers = mkDefaultHandlers();
                } catch (err) {
                    // Non far nulla per ora.
                }
            }),
            switchMap(() => [
                seReportSetCurrentExaminationIdsVisit({ ids: [] }),
                seHeaderToolbarSetIsNavigateOnClickEnabled({ enable: true }),
            ]),
        ),
    );

    logoClick$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(seLogoClick),
                withLatestFrom(this.store.pipe(select(selectCpIsPageOpen))),
                switchMap(([_, isPageOpen]) => {
                    if (!isPageOpen) {
                        return EMPTY;
                    }

                    return this.unlockAllExaminations().pipe(switchMap(() => from(this.routerStackService.home())));
                }),
                catchError(() => of(undefined)),
            ),
        { dispatch: false },
    );

    /**
     * Processo di approve in due passi: prima effettua il salvataggio, poi l'approve.
     */
    startApprove$ = createEffect(() =>
        this.actions$.pipe(
            ofType(cpApprove),
            switchMap(({ useExamService }) => [cpSave({ useExamService }), cpApproveSaving({ useExamService })]),
        ),
    );

    /**
     * Processo vero e proprio di approve: viene lanciato da "cpApproveSaving", ma aspetta fino a "cpSaveCompleted",
     *   per poi iniziare l'approve vero e proprio.
     */
    approve$ = createEffect(() =>
        this.actions$.pipe(
            ofType(cpApproveSaving),
            exhaustMap(({ useExamService }) =>
                this.actions$.pipe(
                    ofType(cpSaveCompleted),
                    take(1),
                    map(({ error }) => ({ error, useExamService })),
                ),
            ),
            filter(({ error }) => !error),
            withLatestFrom(this.store.select(selectCpModules)),
            exhaustMap(([{ useExamService }, modules]) => {
                this.waitingService.isWaiting = true;
                let saveObs: Observable<Json> | undefined;
                if (useExamService) {
                    // Per far felice typescript...
                    const examService = this.reportingDataBuilder.getExamService();
                    if (examService.approveJson) {
                        saveObs = examService.approveJson(this.cpModelService.getAsJson());
                    }
                }
                if (!saveObs) {
                    saveObs = this.reportService.approve();
                }

                return saveObs.pipe(
                    switchMap((model) => [seReportApproveCompleted({ modules }), cpSetModel({ model })]),
                    catchError((err) =>
                        concat(
                            this.handleApproveAndSignErrors(err, true),
                            of(seReportApproveCompleted({ error: true, modules })),
                        ),
                    ),
                    finalize(() => (this.waitingService.isWaiting = false)),
                );
            }),
        ),
    );

    /**
     * Approve di un addendum: Simile ad approve normale, ma non serve salvare il referto perché già definitivo.
     */
    approveAddendum$ = createEffect(() =>
        this.actions$.pipe(
            ofType(cpApproveAddendum),
            exhaustMap(({ addendum, referringPhysician }) =>
                this.reportService
                    .approve({ addendum: addendum ? { ...addendum } : undefined, referringPhysician })
                    .pipe(
                        map((model) => cpSetModel({ model })),
                        catchError((err) => this.handleApproveAndSignErrors(err, true)),
                    ),
            ),
        ),
    );

    /**
     * ReportSave: converti in Save.
     */
    reportSave$ = createEffect(() =>
        this.actions$.pipe(
            ofType(seReportSave),
            map(() => cpSave({})),
        ),
    );

    /**
     * ReportSaveCompleted: converti in CpSaveCompleted.
     */
    reportSaveCompleted$ = createEffect(() =>
        this.actions$.pipe(
            ofType(seReportSaveCompleted),
            map(({ error }) => cpSaveCompleted({ error })),
        ),
    );

    /**
     * Save: lancia gli handler e poi segnala il completamento del salvataggio.
     */
    save$ = createEffect(() =>
        this.actions$.pipe(
            ofType(cpSave),
            exhaustMap(({ forcedReload, useExamService }) => this.save(forcedReload ?? false, useExamService ?? false)),
        ),
    );

    /**
     * Effetto di modifica dei dati del modello.
     */
    patchModel$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(cpPatchModel),
                map(({ patches }) => {
                    for (const binding in patches) {
                        if (Object.hasOwnProperty.call(patches, binding)) {
                            this.cpModelService.setValue(new DataBinding(binding), patches[binding]);
                        }
                    }
                }),
            ),
        { dispatch: false },
    );

    /**
     * Effetto di reinizializzazione del modello.
     */
    setModel$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(cpSetModel),
                map(({ model }) => {
                    this.cpNodeService.restartTabSelector();
                    this.cpModelService.notifyRootChange({ Report: model });
                }),
            ),
        { dispatch: false },
    );

    /**
     * Mostra la modale di errore generica.
     */
    showErrorModal$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(cpShowErrorModal),
                exhaustMap(({ message, title }) =>
                    this.modalDialogNotificationService.error(
                        this.translate.instant(message),
                        this.translate.instant(title),
                    ),
                ),
            ),
        { dispatch: false },
    );

    /**
     * Lancia il flusso di firma.
     */
    sign$ = createEffect(() =>
        this.actions$.pipe(
            ofType(cpSign),
            exhaustMap(({ addendum, print, referringPhysician }) => {
                return this.reportService
                    .sign({ addendum: addendum ? { ...addendum } : undefined, print, referringPhysician })
                    .pipe(
                        switchMap((model) => [
                            cpSetModel({ model }),
                            seReportSaveCompleted({ error: false }),
                            cpUpdateAllNodes({ isModified: false }),
                        ]),
                        catchError((err) => this.handleApproveAndSignErrors(err, false)),
                    );
            }),
        ),
    );

    /**
     * Lancia il flusso di annulla definitivo.
     */
    undoValidation$ = createEffect(() =>
        this.actions$.pipe(
            ofType(cpUndoValidation),
            exhaustMap(() => {
                return this.reportService.undoValidation().pipe(
                    map((model) => cpSetModel({ model })),
                    catchError(() => of(cpEmpty())),
                );
            }),
        ),
    );

    applyNormalValues$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(cpApplyNormalValues),
                withLatestFrom(this.reportService.isModelReadonly$),
                exhaustMap(([_, isReadonly]) => {
                    if (isReadonly) {
                        return of(undefined);
                    }
                    this.constraintApplier.applyNormalValues();

                    return this.modalDialogNotificationService.notify({
                        buttonYes: false,
                        buttonNo: false,
                        buttonCancel: false,
                        buttonOk: true,
                        title: this.translate.instant('Information'),
                        message: this.translate.instant('NormalValuesAppliedSuccess'),
                        type: NotificationType.information,
                    });
                }),
            ),
        { dispatch: false },
    );

    obscurationEditRequest$ = createEffect(() =>
        this.actions$.pipe(
            ofType(cpObscurationEditRequest),
            switchMap(() =>
                this.onObscuration().pipe(
                    catchError(() => of(cpShowErrorModal({ message: 'ECG_ReportSavingFailed', title: 'Error' }))),
                ),
            ),
        ),
    );

    on(event: CpEffectType, handler: CpEffectHandler) {
        this.handlers[event].push(handler);
    }

    off(event: CpEffectType, handler: CpEffectHandler) {
        this.handlers[event] = this.handlers[event].filter((h) => h !== handler);
    }

    // #endregion

    // #region Helpers

    private handleEventResult(result: EventResult): Observable<(unknown | EffectThunk)[]> {
        if (!result) {
            return of([]);
        }

        const imagesToCreate = result.filter(isEffectResultImage).map((img) => ({
            id: img.id,
            data: img.data,
            type: ClinicalRepositoryDataType.Image,
        }));
        const imagesToDelete = result.filter(isEffectResultImageDelete).map((img) => img.id);

        const imagesToCreateRequest =
            imagesToCreate.length === 0 ? of(undefined) : this.cpHttp.saveClinicalImages(imagesToCreate);

        const imagesToDeleteRequest =
            imagesToDelete.length === 0 ? of(undefined) : of(() => this.cpHttp.deleteClinicalImages(imagesToDelete));

        return forkJoin([imagesToCreateRequest, imagesToDeleteRequest]);
    }

    private runPreSaveActions(): Observable<(unknown | EffectThunk)[]> {
        const handlers = Object.values(this.handlers).flat();

        return forkJoin(
            handlers.map((h) =>
                from(h()).pipe(
                    catchError((e) => {
                        this.logger.error(e.message);

                        return of(undefined);
                    }),
                    switchMap((r) => this.handleEventResult(r)),
                ),
            ),
        ).pipe(
            map((res) => res.flat()),
            defaultIfEmpty([]),
        );
    }

    private runPostSaveActions(actions: EffectThunk[]): Observable<unknown> {
        // forkJoin non emette mai un risultato se gli si passa un array vuoto...
        if (actions.length === 0) {
            return of(undefined);
        }

        return forkJoin(actions.map((a) => a())).pipe(
            catchError((e) => {
                this.logger.error(e.message);
                return of(undefined);
            }),
        );
    }

    private scrollPanelIntoView(nodeId: string) {
        const cardElem = document.getElementById(`card_${nodeId}`);

        if (cardElem) {
            cardElem.scrollIntoView({ behavior: 'smooth' });
        }
    }

    private handleApproveAndSignErrors(err: Error, showModal: boolean) {
        if (err instanceof CancellationError) {
            if (err.payload) {
                return of(cpSetModel({ model: err.payload }));
            }
            return of(cpEmpty());
        }

        const actions: Action[] = [];
        if (showModal) {
            actions.push(
                cpShowErrorModal({
                    message: 'ErrorDuringApproving',
                    title: 'Warning',
                }),
            );
        }
        if (err instanceof PartialError) {
            actions.push(cpSetModel({ model: err.newModel }));
        }

        return of(...actions);
    }

    save(forcedReload = false, useExamService = false) {
        if (useExamService) {
            // Per far felice typescript...
            const examService = this.reportingDataBuilder.getExamService();
            const saveFn = examService.saveJson?.bind(examService);
            if (saveFn) {
                return this.saveWithCallback(() => saveFn(this.cpModelService.getAsJson()));
            }
        }
        return this.saveWithCallback(() => this.reportService.save(forcedReload));
    }

    private saveWithCallback(cb: () => Observable<Json>) {
        this.waitingService.isWaiting = true;
        return this.runPreSaveActions().pipe(
            map((postSaveActions) => postSaveActions.filter(isFunction)),
            switchMap((postSaveActions) => {
                return cb().pipe(
                    switchMap((model) => {
                        const effects = [
                            cpSetModel({ model }),
                            seReportSaveCompleted({ error: false }),
                            cpUpdateAllNodes({ isModified: false }),
                        ];

                        return this.runPostSaveActions(postSaveActions).pipe(map(() => effects));
                    }),
                );
            }),
            tapAlways(() => (this.waitingService.isWaiting = false)),
            switchMap((effects) => effects),
            catchError((error) => {
                this.waitingService.isWaiting = false;
                return this.onSaveError(error);
            }),
        );
    }

    // TODO: (STE) any
    private onSaveError(error: any) {
        let message = 'ECG_ReportSavingFailed';
        if (isExtensaLockFault(error?.error)) {
            const userFullName = error?.error?.estensaFaultDetails?.estensaFault?.userFullName
                ? ' (' + error.error.estensaFaultDetails.estensaFault.userFullName + ')'
                : '';
            message = this.translate.instant('ExaminationToSaveAreLocked') + userFullName;
            this.store.dispatch(cpNavigationBack({ requestConfirmation: false }));
        }

        return [cpShowErrorModal({ message, title: 'Warning' }), seReportSaveCompleted({ error: true })];
    }

    private onCpInitialize(state: CpPageState, isPreview: boolean) {
        const actions: Action[] = [
            seToolbarSetRightSlider({
                status: {
                    checked: !state.rightSliderClosed,
                    enabled: state.rightSliderEnabled,
                    visible: state.rightSliderEnabled,
                    overlap: false,
                },
            }),
            seToolbarSetLeftSlider({
                status: {
                    checked: !state.leftSliderClosed,
                    enabled: true,
                    visible: true,
                    overlap: false,
                },
            }),
            seReportSetPatientId({
                patientId: this.reportService.patientId ?? null,
            }),
            seReportSetKeyImages({
                keyImages: this.reportService.keyImages,
            }),
            seReportSetImagesPerRowInPlaceholder({
                imagesPerRowInPlaceholder: this.reportService.imagesForRowInPlaceholder,
            }),
            seReportSetIsPreview({
                isPreview,
            }),
            seReportSetCurrentExaminationIdsVisit({ ids: this.reportService.examinationIds ?? [] }),
            seHeaderToolbarSetIsNavigateOnClickEnabled({ enable: false }),
        ];

        if (state.rightSliderEnabled && state.sideComponents.length > 0) {
            actions.push(
                seSideNavComponentSetActiveComponents({
                    activationList: state.sideComponents,
                }),
            );
        }

        return actions;
    }

    private async onMessage(message: any) {
        let response: estensaPostMessage = {
            estensaPostMessageId: -1, // Lo assegna il servizioperchè deve corrispondere a quello della RQ
            estensaPostMessageType: THIRD_PART_POST_MESSAGES.UNKNOWN,
        };

        switch (message.messageType) {
            case 'CpCanSave':
                response.estensaPostMessageType = THIRD_PART_POST_MESSAGES.CAN_SAVE_CONTEXT_RSP;

                if (!this.reportService.canSave) {
                    response.statusCode = THIRD_PART_STATUS_CODE.CANNOT_SAVE;
                    response.statusDetail = THIRD_PART_STATUS_DETAIL.CANNOT_SAVE;
                } else {
                    response.statusCode = THIRD_PART_STATUS_CODE.SUCCESS;
                    response.statusDetail = THIRD_PART_STATUS_DETAIL.SUCCESS;
                }

                message.data.reply(response);
                break;
            case 'CpSave':
                response.estensaPostMessageType = THIRD_PART_POST_MESSAGES.SAVE_CONTEXT_RSP;

                if (!this.reportService.canSave) {
                    response.statusCode = THIRD_PART_STATUS_CODE.CANNOT_SAVE;
                    response.statusDetail = THIRD_PART_STATUS_DETAIL.CANNOT_SAVE;
                    return message.data.reply(response);
                }

                try {
                    await lastValueFrom(
                        this.save().pipe(
                            tap((action) => {
                                this.store.dispatch(action);
                            }),
                        ),
                    );

                    response.statusCode = THIRD_PART_STATUS_CODE.SUCCESS;
                    response.statusDetail = THIRD_PART_STATUS_DETAIL.SUCCESS;
                    message.data.reply(response);
                } catch {
                    response.statusCode = THIRD_PART_STATUS_CODE.CANNOT_SAVE;
                    response.statusDetail = THIRD_PART_STATUS_DETAIL.CANNOT_SAVE_INTERNAL;

                    message.data.reply(response);
                }
                break;
            case 'CpDiscard':
                response.estensaPostMessageType = THIRD_PART_POST_MESSAGES.DISCARD_CONTEXT_RSP;
                try {
                    this.uiEngine.safeExit();
                    await this.routerStackService.go('/new/vnext/third-part-waiting');

                    response.statusCode = THIRD_PART_STATUS_CODE.SUCCESS;
                    response.statusDetail = THIRD_PART_STATUS_DETAIL.SUCCESS;
                } catch {
                    response.statusCode = THIRD_PART_STATUS_CODE.CANNOT_DISCARD;
                    response.statusDetail = THIRD_PART_STATUS_DETAIL.CANNOT_DISCARD_INTERNAL;
                }
                message.data.reply(response);
                break;
            case 'CpLoadContext':
                response.estensaPostMessageType = THIRD_PART_POST_MESSAGES.LOAD_CONTEXT_RSP;
                try {
                    // Verifica che non ci sia un context attivo
                    const isOpen = await firstValueFrom(this.store.pipe(select(selectCpIsPageOpen), take(1)));
                    if (isOpen) {
                        response.statusCode = THIRD_PART_STATUS_CODE.ERROR_CONTEXT_OPEN;
                        response.statusDetail = THIRD_PART_STATUS_DETAIL.CANNOT_LOAD_CONTEXT_OPEN;
                    } else {
                        this.thirdPartSupportService.goToReporting(message.data.examinationId);

                        response.statusCode = THIRD_PART_STATUS_CODE.SUCCESS;
                        response.statusDetail = THIRD_PART_STATUS_DETAIL.SUCCESS;
                    }
                } catch {
                    response.statusCode = THIRD_PART_STATUS_CODE.ERROR_CONTEXT_OPEN;
                    response.statusDetail = THIRD_PART_STATUS_DETAIL.CANNOT_LOAD_CONTEXT;
                }
                message.data.reply(response);
                break;
        }
    }

    private unlockAllExaminations() {
        const report = this.cpModelService.getEntity(HardcodedData.bindings.report);
        const examinations = report?.getPropertyValue('Examinations');
        if (!examinations || !(examinations instanceof CpEntityList)) {
            return of(undefined);
        }

        return this.reportingDataBuilder.getExamService().unlock(
            examinations.activeEntities.map((i: any) => {
                return { idExamination: i.Id };
            }),
        );
    }

    private onObscuration() {
        const report = this.cpModelService.getAsJson().Report;
        const isApproved = report.ReferenceExamination.IsApproved;
        const data = {
            ...convertPropertiesToCamelCase<ObscuredData>(report.HardCodedData),
        };

        return this.obscuredDataService.edit(data, isApproved).pipe(
            switchMap((result) => {
                if (!result) {
                    return of(cpEmpty());
                }

                // Gestione FSE 2.0
                const needsToSave =
                    this.settings.radioSettings?.reportConfiguration.obscuredTabType === ObscuredTabType.FSE20 &&
                    isApproved;

                const obs = needsToSave ? this.reportService.saveHardCodedData(result) : of(result);

                return obs.pipe(
                    tap((hardCodedData) => {
                        this.cpModelService.setValue(
                            HardcodedData.bindings.hardCodedData,
                            convertPropertiesToPascalCase(hardCodedData),
                        );
                    }),
                    map(() => cpEmpty()),
                );
            }),
        );
    }

    // #endregion
}
